import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-white" data-testid="reset-missing-token">
        <div className="max-w-md text-center">
          <h2 className="font-display font-black text-4xl tracking-tighter mb-4">Invalid link.</h2>
          <p className="font-mono text-sm text-[var(--text-secondary)] mb-6">This reset link is missing a token.</p>
          <Link to="/forgot-password" className="inline-block bg-black text-white px-6 py-3 font-display font-bold tracking-wider hover:bg-[var(--alert-red)] transition-colors">
            REQUEST A NEW LINK →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-black text-white p-10 flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[var(--alert-red)] flex items-center justify-center font-display font-black">!</div>
          <span className="font-display font-black tracking-tighter text-xl">RED.FLAG</span>
        </div>
        <div>
          <div className="overline text-white/60 mb-4">SET NEW PASSWORD</div>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tighter leading-[0.9]">
            ALMOST <br />
            <span className="text-[var(--alert-red)]">DONE.</span>
          </h1>
          <p className="mt-6 font-mono text-sm max-w-md text-white/70">
            Pick a strong password. At least 6 characters. Use something unique.
          </p>
        </div>
        <div className="overline text-white/40">YOU'LL BE SIGNED IN NEXT.</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        {done ? (
          <div className="w-full max-w-md text-center" data-testid="reset-success">
            <div className="overline mb-2">DONE</div>
            <h2 className="font-display font-black text-4xl tracking-tighter mb-4">Password reset.</h2>
            <p className="font-mono text-sm text-[var(--text-secondary)] mb-6">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="reset-form">
            <div className="overline mb-2">NEW PASSWORD</div>
            <h2 className="font-display font-black text-4xl tracking-tighter mb-8">Pick a new one.</h2>

            <label className="block mb-6">
              <div className="overline mb-2">NEW PASSWORD (MIN 6)</div>
              <input
                data-testid="reset-password-input"
                type="password" minLength={6} required autoComplete="new-password"
                value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
              />
            </label>
            <label className="block mb-6">
              <div className="overline mb-2">CONFIRM PASSWORD</div>
              <input
                data-testid="reset-confirm-input"
                type="password" minLength={6} required autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
              />
            </label>

            <button
              data-testid="reset-submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50"
            >
              {loading ? "UPDATING…" : "UPDATE PASSWORD →"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
